import { useQuery } from '@tanstack/react-query'

import { API } from '@/api'
import { LoginScope, TeamRole } from '@/consts/team'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { useScopeInfo, useTeamTokenData } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { getStorageBillingMode } from '@/utils/storage-billing'

export function useBillingAnnouncement() {
  const { mode, teamResID, memberResID } = useUnifiedContext()
  const userID = useUserStore((state) => state.id)
  const scopeInfo = useScopeInfo()
  const teamTokenData = useTeamTokenData()

  // Wait for the layout and token scope to agree before querying /subvolumes.
  const scopeReady =
    userID > 0 &&
    (mode === 'team'
      ? !!teamResID &&
        !!memberResID &&
        teamTokenData?.teamResID === teamResID &&
        scopeInfo?.scope === LoginScope.Team &&
        scopeInfo.scopeID === teamTokenData.teamId &&
        scopeInfo.resID === memberResID
      : !teamTokenData && scopeInfo?.scope !== LoginScope.Team)
  const canReadShared =
    scopeReady &&
    mode === 'team' &&
    (teamTokenData?.userRole === TeamRole.Owner || teamTokenData?.userRole === TeamRole.Admin)

  const queryOptions = {
    staleTime: 60_000,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: 'always' as const,
    retry: false,
  }

  const personal = useQuery({
    ...queryOptions,
    queryKey: ['billing-announcement', 'personal', mode, userID, teamResID, memberResID],
    enabled: scopeReady,
    queryFn: async ({ signal }) => {
      const res = await API.getVolumes(undefined, { signal, errMsg: false, noErrMsg: true })
      if (res.code !== 0) throw new Error('Failed to load personal billing mode')
      return getStorageBillingMode(res.volumes)
    },
  })

  const shared = useQuery({
    ...queryOptions,
    queryKey: ['billing-announcement', 'shared', mode, userID, teamResID, memberResID],
    enabled: canReadShared,
    queryFn: async ({ signal }) => {
      if (!teamResID) throw new Error('Team is required to load shared billing mode')
      const res = await API.getTeamInfo(
        { res_id: teamResID },
        { signal, errMsg: false, noErrMsg: true },
      )
      if (res.code !== 0 || !res.team?.volumes) {
        throw new Error('Failed to load shared billing mode')
      }
      return getStorageBillingMode(res.team.volumes.subParts)
    },
  })

  return {
    personalMode:
      scopeReady && !personal.isFetching && !personal.isError ? personal.data : undefined,
    teamSharedMode:
      canReadShared && !shared.isFetching && !shared.isError ? shared.data : undefined,
  }
}
